import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';

import { Modal, ResponsiveWidth } from '../../../../common';
import { Button } from '../../../../common/button-new';
import { Form } from '../../../../common/form';
import { TextField } from '../../../../common/form/text-field';
import { useFirstNameValidation } from '../../../../../hooks/use-first-name-validation';
import { usePhoneNumberValidation } from '../../../../../hooks/use-phone-number-validation/use-phone-number-validation';
import { consumerApi } from '../../../../../rest/consumer';
import { v2026PP10366 } from '../../../../../constants/veeva';
import { yup } from '../../../../../services/yup';
import { CvCallbackRequestModalProps } from './cv-callback-request-modal.models';
import { useStyles } from './cv-callback-request-modal.styles';
import { blurActiveElement } from '../../../../../utils';
import { GET_PROFILE } from '../../../../../graphql/profile/queries/index';
import { IFormImperativeHandleProps } from '../../../../common/form/form.models';

export const CvCallbackRequestModal = ({ onClose, onSuccess, ...props }: CvCallbackRequestModalProps): JSX.Element => {
  const classes = useStyles();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const { data, loading } = useQuery(GET_PROFILE);
  const firstName = data?.profile?.firstName ?? '';
  const phoneNumber = data?.profile?.mobile?.replace(/^\+/, '') ?? '';
  const firstNameValidation = useFirstNameValidation();
  const phoneValidation = usePhoneNumberValidation();
  
  const validationSchema = useMemo(() => yup.object({
    firstName: firstNameValidation,
    phoneNumber: phoneValidation,
  }), [firstNameValidation, phoneValidation]);

  const defaultValues = useMemo(() => ({
    firstName,
    phoneNumber,
  }), [firstName, phoneNumber]);

  useEffect(() => {
    if (!loading && firstName && phoneNumber) {
      setTimeout(() => {
        formRef.current?.trigger();
      }, 0);
    }
  }, [loading, firstName, phoneNumber]);

  const handleSubmit = useCallback(async ({ firstName: name, phoneNumber: phone }: { firstName: string; phoneNumber: string }): Promise<void> => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await consumerApi.callbackRequest({
        name: name,
        mobile: phone.replace(/\D/g, ''),
      });

      blurActiveElement();
      onSuccess?.();
    } catch (error) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      throw error;
    }
  }, [onSuccess]);

  if (loading) return <></>;

  return (
    <Modal
      {...props}
      responsiveWidth={ResponsiveWidth.sm}
      onClose={onClose}
      title={<FormattedMessage id="common.request.title" />}
      titleStyles={classes.title}
      propsPaperStyles={classes.paper}
      propsContentStyles={classes.content}
      veevaNumber={v2026PP10366}
      isVeevaSmall={true}
      content={(
        <Form
          ref={formRef}
          key={`${firstName}-${phoneNumber}`}
          onSubmit={handleSubmit}
          defaultValues={defaultValues}
          validationSchema={validationSchema}
          validationMode="onChange"
          onValidateDependencies={(isValid) => setIsSubmitDisabled(!isValid)}
        >
          <div className={classes.fields}>
            <div className={classes.firstField}>
              <TextField
                name="firstName"
                label={<FormattedMessage id="common.firstName" />}
                isRequired
                InputLabelProps={{ shrink: true }}
                fullWidth
                autoFocus
              />
            </div>
            <div className={classes.secondField}>
              <TextField
                name="phoneNumber"
                label={<FormattedMessage id="profile.telephoneTextField.label" />}
                isRequired
                type="tel"
                InputLabelProps={{ required: true }}
                fullWidth
              />
            </div>
            <Button
              type="submit"
              style="cv"
              testId="cv-callback-submit-button"
              rootClasses={classes.submitButton}
              disabled={isSubmitDisabled || isSubmitting}
            >
              <FormattedMessage id="footer.supportService.requestCallButton.label" />
            </Button>
          </div>
        </Form>
      )}
    />
  );
};